import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Tooltip, message } from 'antd';
import styled from 'styled-components';
import { RootState } from '../../../../store';
import {
  getCompliancePurposesFullReport,
  parseCompliancePurposes,
  type CompliancePurpose,
} from '../../../../services/riskAssessmentService';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
} from '@syncfusion/ej2-react-grids';

// ==================== STYLED COMPONENTS ====================

const TabContent = styled.div`
  min-height: 400px;
  margin-top: 16px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #E5E7EB;
    border-top-color: #2563EB;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const EmptyStateWrapper = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 6px;
`;

const EmptyStateText = styled.div`
  color: #1F2937;
  margin-bottom: 16px;
  font-size: 15px;
  font-weight: 500;
`;

const GridWrapper = styled.div`
  background: #E0E7FF;
  border-radius: 6px;
  border: 1px solid #E0E7FF;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);  
  overflow: hidden;
  padding: 0 0 0px 2px;

  .e-grid {
    border: none;
  }

  .e-grid .e-headercell {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    font-size: 10px;
    color: #6B7280;
    padding: 6px 8px;
    border-color: #E5E7EB;
    border-right: 1px solid #E5E7EB;
  }

  .e-grid .e-headercell:last-child {
    border-right: none;
  }

  .e-grid .e-rowcell {
    border-color: #E5E7EB;
    border-right: 1px solid #E5E7EB;
    padding: 12px 8px;
    font-size: 12px;
    color: #1F2937;
    white-space: normal;
    word-break: break-word;
    line-height: 1.5;
    vertical-align: top;
  }

  .e-grid .e-rowcell:last-child {
    border-right: none;
  }

  .e-grid .e-emptyrow {
    display: none;
  }

  .e-grid .e-row.e-active .e-rowcell,
  .e-grid .e-row.e-selectionbackground .e-rowcell,
  .e-grid .e-row.e-active,
  .e-grid .e-row.e-selectionbackground {
    background: transparent !important;
  }
`;

const IndexCell = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 12px;
  border: 1px solid #E5E7EB;
  border-radius: 14px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #6B7280;
`;

const ObjectivesCell = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  overflow: hidden;
  max-height: 64px;
`;

const ObjectiveGroupTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: #DBEAFE;
  }
`;

const ObjectiveGroupProcessCode = styled.span`
  font-weight: 600;
  margin-right: 2px;
`;

const ObjectiveGroupNumbers = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
`;

const ObjectiveGroupNumberCircle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 4px;
  background: transparent;
  color: #2563EB;
  border: 1px solid #D1D5DB;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 600;
`;

const ControlChip = styled.span<{ $clickable?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  margin-right: 4px;
  margin-bottom: 4px;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: all 0.2s;
  
  &:hover {
    ${props => props.$clickable && `
      background: #DBEAFE;
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(30, 64, 175, 0.15);
    `}
  }
`;

const TooltipTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #1F2937;
  text-align: center;
  margin-bottom: 12px;
`;

const TooltipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TooltipItem = styled.div`
  padding: 8px 12px;
  background: #EFF6FF;
  border-radius: 6px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #2563EB;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #DBEAFE;
    transform: translateX(2px);
  }
`;

// ==================== MAIN COMPONENT ====================

const RiskAssessmentFraudRisksTotal: React.FC = () => {
  const navigate = useNavigate();
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  const { cycles: riskAssessmentCycles } = useSelector((state: RootState) => state.riskAssessment);
  const annualReportId = selectedReport?.annualReportId;

  const [compliancePurposes, setCompliancePurposes] = useState<CompliancePurpose[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const hasFetchedRef = useRef(false);
  const currentReportIdRef = useRef<string | undefined>(undefined);

  const fetchCompliancePurposes = useCallback(async () => {
    if (!annualReportId) return;

    setIsLoading(true);
    try {
      const response = await getCompliancePurposesFullReport(annualReportId, {
        skip: 0,
        take: 1000,
        requiresCounts: true,
      });
      const parsed = parseCompliancePurposes(response.result || []);
      setCompliancePurposes(parsed);
    } catch (error: any) {
      console.error('Failed to fetch compliance purposes full report:', error);
      message.error(error?.message || 'Failed to load fraud risks');
    } finally {
      setIsLoading(false);
    }
  }, [annualReportId]);

  useEffect(() => {
    if (!annualReportId) return;

    const isNewReport = currentReportIdRef.current !== annualReportId;
    if (isNewReport) {
      hasFetchedRef.current = false;
      currentReportIdRef.current = annualReportId;
    }

    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchCompliancePurposes();
  }, [annualReportId, fetchCompliancePurposes]);

  // ==================== COLUMN TEMPLATES ====================

  const indexTemplate = (props: CompliancePurpose) => {
    return <IndexCell>{props.indexStr}</IndexCell>;
  };

  const factorsTemplate = (props: CompliancePurpose) => {
    return <div>{props.specificFraudRiskFactors}</div>;
  };

  const objectivesTemplate = (props: CompliancePurpose) => {
    if (!props.objectiveNumbers || props.objectiveNumbers.length === 0) {
      return <span style={{ color: '#9CA3AF' }}>-</span>;
    }

    const grouped: Record<string, string[]> = {};
    props.objectiveNumbers.forEach((num, idx) => {
      const processCode = props.objectiveProcessCodes?.[idx] || '?';
      if (!grouped[processCode]) grouped[processCode] = [];
      grouped[processCode].push(num);
    });

    const groupEntries = Object.entries(grouped);

    const renderChips = () => groupEntries.map(([processCode, numbers]) => {
      const matchingRa = riskAssessmentCycles
        .flatMap(c => c.riskAssessments)
        .find(ra => ra.processCode === processCode);
      const chipAssessmentId = matchingRa?.id;

      return (
        <ObjectiveGroupTag
          key={processCode}
          onClick={(e) => {
            e.stopPropagation();
            if (chipAssessmentId) {
              navigate(`/risk-assessment/${chipAssessmentId}/tab2`, { replace: true });
            }
          }}
          title={chipAssessmentId ? `Click to view objectives for ${processCode}` : processCode}
        >
          <ObjectiveGroupProcessCode>{processCode}</ObjectiveGroupProcessCode>
          <ObjectiveGroupNumbers>
            {[...numbers]
              .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
              .map((num, i) => (
                <ObjectiveGroupNumberCircle key={i}>{num}</ObjectiveGroupNumberCircle>
              ))}
          </ObjectiveGroupNumbers>
        </ObjectiveGroupTag>
      );
    });

    const tooltipContent = (
      <div>
        <TooltipTitle>Objectives</TooltipTitle>
        <TooltipList>
          {groupEntries.map(([processCode, numbers]) => {
            const sorted = [...numbers].sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
            const matchingRa = riskAssessmentCycles
              .flatMap(c => c.riskAssessments)
              .find(ra => ra.processCode === processCode);
            const chipAssessmentId = matchingRa?.id;
            return (
              <TooltipItem
                key={processCode}
                onClick={(e) => {
                  e.stopPropagation();
                  if (chipAssessmentId) {
                    navigate(`/risk-assessment/${chipAssessmentId}/tab2`, { replace: true });
                  }
                }}
                style={{ cursor: chipAssessmentId ? 'pointer' : 'default' }}
              >
                {processCode}: {sorted.join(', ')}
              </TooltipItem>
            );
          })}
        </TooltipList>
      </div>
    );

    return (
      <Tooltip
        title={tooltipContent}
        placement="top"
        overlayStyle={{ maxWidth: '400px' }}
        overlayInnerStyle={{
          backgroundColor: '#FFFFFF',
          padding: '16px',
          borderRadius: '12px',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
        }}
        color="#FFFFFF"
      >
        <ObjectivesCell>
          {renderChips()}
        </ObjectivesCell>
      </Tooltip>
    );
  };

  const handleNavigateToControl = (controlId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/control-activities/${controlId}`);
  };

  const controlsTemplate = (props: CompliancePurpose) => {
    if (!props.controlCodes || props.controlCodes.length === 0) {
      return <span style={{ color: '#9CA3AF' }}>-</span>;
    }

    const controlsContent = (
      <div>
        {props.controlCodes.map((code, index) => {
          const controlId = props.fraudRiskMitigatingControls?.[index];
          return (
            <ControlChip
              key={index}
              $clickable={!!controlId}
              onClick={(e) => controlId && handleNavigateToControl(controlId, e)}
              title={controlId ? 'Click to view control details' : undefined}
            >
              {code}
            </ControlChip>
          );
        })}
      </div>
    );

    if (props.controlCodes.length > 1) {
      const tooltipContent = (
        <div>
          <TooltipTitle>Fraud Risk Mitigating Controls</TooltipTitle>
          <TooltipList>
            {props.controlCodes.map((code, index) => {
              const controlId = props.fraudRiskMitigatingControls?.[index];
              return (
                <TooltipItem
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (controlId) handleNavigateToControl(controlId, e);
                  }}
                  style={{ cursor: controlId ? 'pointer' : 'default' }}
                >
                  {code}
                </TooltipItem>
              );
            })}
          </TooltipList>
        </div>
      );

      return (
        <Tooltip
          title={tooltipContent}
          placement="top"
          overlayStyle={{ maxWidth: '400px' }}
          overlayInnerStyle={{
            backgroundColor: '#FFFFFF',
            padding: '16px',
            borderRadius: '12px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
          }}
          color="#FFFFFF"
        >
          {controlsContent}
        </Tooltip>
      );
    }

    return controlsContent;
  };

  // ==================== RENDER ====================

  if (isLoading) {
    return (
      <TabContent>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}>
          <LoadingSpinner>
            <div className="spinner" />
          </LoadingSpinner>
        </div>
      </TabContent>
    );
  }

  if (compliancePurposes.length === 0) {
    return (
      <TabContent>
        <EmptyStateWrapper>
          <EmptyStateText>No fraud risk items found across all processes.</EmptyStateText>
        </EmptyStateWrapper>
      </TabContent>
    );
  }

  return (
    <TabContent>
      <GridWrapper>
        <GridComponent
          key={`fraud-risks-total-grid-${compliancePurposes.length}`}
          dataSource={compliancePurposes}
          allowPaging={false}
          enableHover={true}
        >
          <ColumnsDirective>
            <ColumnDirective
              field='index'
              headerText='#'
              width='100'
              textAlign='Center'
              template={indexTemplate}
            />
            <ColumnDirective
              field='specificFraudRiskFactors'
              headerText='Specific Fraud Risk Factors'
              textAlign='Left'
              template={factorsTemplate}
            />
            <ColumnDirective
              field='objectiveNumbers'
              headerText='Objectives'
              width='240'
              textAlign='Left'
              template={objectivesTemplate}
            />
            <ColumnDirective
              field='controlCodes'
              headerText='Fraud Risk Mitigating Controls'
              width='360'
              textAlign='Left'
              template={controlsTemplate}
            />
          </ColumnsDirective>
          <Inject services={[Page]} />
        </GridComponent>
      </GridWrapper>
    </TabContent>
  );
};

export default RiskAssessmentFraudRisksTotal;
